import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Search, Building2, FileText, FolderKanban, CheckSquare, Users, Plus, LayoutDashboard, Target, Kanban, UserCircle } from "lucide-react";

const QUICK_ACTIONS = [
  { label: "Create Brief", icon: Plus, path: "/bizdev/briefs?new=1", color: "#E31937" },
  { label: "My Day", icon: LayoutDashboard, path: "/me", color: "#06B6D4" },
  { label: "All Leads", icon: Target, path: "/bizdev/leads", color: "#22C55E" },
  { label: "Briefs Board", icon: Kanban, path: "/bizdev/briefs", color: "#A855F7" },
  { label: "Accounts", icon: Building2, path: "/bizdev/accounts", color: "#F59E0B" },
  { label: "My Tasks", icon: CheckSquare, path: "/tasks", color: "#EC4899" },
  { label: "Projects", icon: FolderKanban, path: "/projects", color: "#3B82F6" },
  { label: "Team", icon: Users, path: "/admin", color: "#64748B" },
];

export default function GlobalSearch({ open, onOpen, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // CMD+K keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); onOpen(); }
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpen, onClose]);

  useEffect(() => {
    if (open) { setQuery(""); setResults(null); setActiveIdx(-1); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults(null); setActiveIdx(-1); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try { setResults((await api.get(`/search?q=${encodeURIComponent(query)}`)).data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const TYPE_ICONS = { lead: Building2, brief: FileText, project: FolderKanban, task: CheckSquare, person: Users };
  const TYPE_COLORS = { lead: "#06B6D4", brief: "#EAB308", project: "#A855F7", task: "#22C55E", person: "#EC4899" };

  const handleSelect = (type, id) => {
    onClose();
    const routes = { lead: `/bizdev/leads/${id}`, brief: `/bizdev/briefs/${id}`, project: `/projects/${id}`, task: `/tasks/${id}`, person: `/team/${id}` };
    navigate(routes[type] || "/me");
  };

  const handleAction = (path) => {
    onClose();
    navigate(path);
  };

  const sections = results ? [
    { type: "person", label: "People", items: (results.people || []).map(p => ({...p, user_id: p.user_id})), idKey: "user_id", nameKey: "name" },
    { type: "lead", label: "Leads", items: results.leads || [], idKey: "lead_id", nameKey: "company_name" },
    { type: "brief", label: "Briefs", items: results.briefs || [], idKey: "brief_id", nameKey: "title" },
    { type: "project", label: "Projects", items: results.projects || [], idKey: "project_id", nameKey: "title" },
    { type: "task", label: "Tasks", items: results.tasks || [], idKey: "task_id", nameKey: "title" },
  ].filter(s => s.items.length > 0) : [];

  // Build flat list for keyboard nav
  const flatItems = [];
  if (!query) {
    // Filter quick actions by query-like matching
    QUICK_ACTIONS.forEach((a, i) => flatItems.push({ kind: "action", idx: i, ...a }));
  } else {
    sections.forEach(s => s.items.forEach(item => flatItems.push({ kind: "result", type: s.type, id: item[s.idKey], name: item[s.nameKey], ...item })));
  }

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && activeIdx >= 0 && activeIdx < flatItems.length) {
      e.preventDefault();
      const item = flatItems[activeIdx];
      if (item.kind === "action") handleAction(item.path);
      else handleSelect(item.type, item.id);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]" data-testid="global-search">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 cinema-enter">
        <div className="glass-card rounded-2xl overflow-hidden shadow-2xl" >
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
            <Search size={18} className="text-[var(--t3)]" />
            <input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setActiveIdx(-1); }} onKeyDown={handleKeyDown} placeholder="Search or jump to..." className="flex-1 bg-transparent text-white text-sm placeholder:text-white/20 focus:outline-none" />
            <kbd className="text-[12px] text-white/20 border border-[var(--bdr)] rounded px-1.5 py-0.5 hidden sm:inline">ESC</kbd>
          </div>

          {/* Quick Actions (when no query) */}
          {!query && (
            <div className="p-2">
              <p className="text-white/20 text-[11px] uppercase tracking-wider px-3 py-1.5 font-mono">Quick Actions</p>
              {QUICK_ACTIONS.map((a, i) => {
                const Icon = a.icon;
                const isActive = activeIdx === i;
                return (
                  <button key={a.label} onClick={() => handleAction(a.path)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-colors ${isActive ? "bg-white/[0.08]" : "hover:bg-white/[0.05]"}`}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: a.color + "15" }}>
                      <Icon size={14} style={{ color: a.color }} />
                    </div>
                    <span className="text-white/80 text-sm normal-case">{a.label}</span>
                    {i === 0 && <span className="ml-auto text-white/15 text-[11px] font-mono">N</span>}
                  </button>
                );
              })}
            </div>
          )}

          {loading && <div className="px-5 py-3 text-[var(--t3)] text-xs font-mono">Searching...</div>}

          {/* Search Results */}
          {sections.length > 0 && (
            <div className="max-h-80 overflow-y-auto custom-scrollbar p-2">
              {(() => { let flatIdx = 0; return sections.map(s => (
                <div key={s.type}>
                  <p className="text-white/20 text-[11px] uppercase tracking-wider px-3 py-1.5 font-mono">{s.label}</p>
                  {s.items.map(item => {
                    const Icon = TYPE_ICONS[s.type];
                    const color = TYPE_COLORS[s.type];
                    const currentIdx = flatIdx++;
                    const isActive = activeIdx === currentIdx;
                    return (
                      <button key={item[s.idKey]} onClick={() => handleSelect(s.type, item[s.idKey])}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-colors ${isActive ? "bg-white/[0.08]" : "hover:bg-white/[0.05]"}`}>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
                          <Icon size={14} style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white/80 text-sm truncate normal-case">{item[s.nameKey]}</p>
                          <p className="text-white/25 text-[13px]">{item.status || item.funnel_stage || ""}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )); })()}
            </div>
          )}

          {query.length >= 2 && sections.length === 0 && !loading && (
            <div className="px-5 py-8 text-center"><p className="text-white/20 text-sm normal-case">No results for "{query}"</p></div>
          )}

          {/* Footer hint */}
          <div className="px-5 py-2.5 border-t border-white/5 flex items-center gap-4">
            <span className="text-white/15 text-[11px] font-mono">↑↓ navigate</span>
            <span className="text-white/15 text-[11px] font-mono">↵ select</span>
            <span className="text-white/15 text-[11px] font-mono">esc close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
